import { z } from 'zod'

// Validation schemas
export const ViolationSchema = z.object({
  type: z.string(),
  severity: z.enum(['critical', 'high', 'medium', 'low']),
  component: z.string(),
  file_path: z.string(),
  line_number: z.number(),
  actual: z.string(),
  expected: z.string(),
  fix_suggestion: z.string(),
  auto_fixable: z.boolean(),
  confidence: z.number(),
})

export const ScanResultSchema = z.object({
  scan_id: z.string(),
  app_name: z.string(),
  timestamp: z.string(),
  compliance_score: z.number(),
  violations: z.array(ViolationSchema),
  recommendations: z.array(z.string()),
  auto_fixes_available: z.number(),
  components_scanned: z.number(),
  mode: z.string(),
})

export type Violation = z.infer<typeof ViolationSchema>
export type ScanResult = z.infer<typeof ScanResultSchema>

// Mock data for testing when database is not available
export function getMockScanData(): ScanResult {
  return {
    scan_id: crypto.randomUUID(),
    app_name: 'intelligence',
    timestamp: new Date().toISOString(),
    compliance_score: 0.774,
    violations: [
      {
        type: 'incorrect_dimension_name',
        severity: 'critical',
        component: 'RiskMatrix',
        file_path: 'src/components/RiskMatrix.tsx',
        line_number: 45,
        actual: 'Threat Detection',
        expected: 'Time to Revenue Disruption',
        fix_suggestion: 'Replace with correct FMS dimension name',
        auto_fixable: true,
        confidence: 0.95,
      },
      {
        type: 'hardcoded_framework_data',
        severity: 'high',
        component: 'BusinessModelSelector',
        file_path: 'src/components/BusinessModelSelector.tsx',
        line_number: 23,
        actual: 'custodio_financiero',
        expected: 'Value from FMS Provider',
        fix_suggestion: 'Use useFMSData() hook instead of hardcoded value',
        auto_fixable: false,
        confidence: 0.8,
      },
      {
        type: 'incorrect_archetype_form',
        severity: 'high',
        component: 'ArchetypeDisplay',
        file_path: 'src/components/ArchetypeDisplay.tsx',
        line_number: 67,
        actual: 'custodios',
        expected: 'custodio',
        fix_suggestion: 'Use singular form as per FMS Contract V4',
        auto_fixable: true,
        confidence: 0.98,
      },
    ],
    recommendations: [
      'Adopt FMS Provider pattern across all components',
      'Update dimension names to match FMS Contract V4.1.0',
      'Replace hardcoded framework data with FMS Provider hooks',
    ],
    auto_fixes_available: 2,
    components_scanned: 15,
    mode: 'quick',
  }
}

// Get platform-wide metrics
export async function getPlatformMetrics() {
  // In production, this would fetch from database
  return {
    platform_score: 0.82,
    total_scans_today: 47,
    violations_caught: 156,
    auto_fixes_applied: 89,
    apps_monitored: [
      { name: 'intelligence', score: 0.774, status: 'violations' },
      { name: 'assessment', score: 0.92, status: 'compliant' },
      { name: 'benchmark', score: 0.88, status: 'violations' },
      { name: 'framework', score: 0.95, status: 'compliant' },
    ],
    trend: 'improving' as const,
  }
}

// Get historical metrics for charts
export async function getHistoricalMetrics() {
  // Mock data for the last 7 days
  const days = 7
  const data = []
  const now = new Date()

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now)
    date.setDate(date.getDate() - i)

    data.push({
      date: date.toISOString().split('T')[0],
      compliance_score: 0.7 + (Math.random() * 0.2) + (0.02 * (days - i)),
      violations: Math.floor(150 + (Math.random() * 50) - (5 * (days - i))),
      scans: Math.floor(40 + (Math.random() * 20)),
    })
  }

  return data
}